import React, { useState } from 'react';
import { observer } from 'mobx-react';

import { useStore } from 'stores';
import { Feature } from 'stores/FeatureStore';
import { ActionIcon, CancelIcon, DownArrowIcon } from 'components/Icon';
import { Toggle } from 'components/Form/Toggle';
import { SuggestionsSelector } from 'components/Form/SuggestionsSelector';
import { WithAddon } from 'components/Form/WithAddon';
import { ErrorMessage, Message } from 'components/Message';

class FeatureSelection
{
  feature?: Feature;
}

interface Props
{
  onSuccess?: (feature: Feature) => void,
}

export const ToggleCreator: React.FC<Props> = observer(({onSuccess}) =>
{
  const { uiStore, toggleStore, featureStore} = useStore();

  const [featureInput, setFeatureInput] = useState('');

  const [isToggleOn, setIsToggleOn] = useState(true);

  const [errorMessage, setErrorMessage] = useState('');
  const [featureSuggestions, setFeatureSuggestions] = useState<Feature[]>([]);
  const [featureSuggestionsLoading, setFeatureSuggestionsLoading] = useState(false);
  const [featureSuggestionsVisible, setFeatureSuggestionsVisible] = useState(false);
  const [selectedFeature] = useState<FeatureSelection>({});


  const [scopeInput, setScopeInput] = useState('');
  const [scopeSuggestions, setScopeSuggestions] = useState<string[]>([]);
  const [scopeSuggestionsLoading, setScopeSuggestionsLoading] = useState(false);
  const [scopeSuggestionsVisible, setScopeSuggestionsVisible] = useState(false);


  const featureInputChangeHandler = async (newValue: string) =>
  {
    setFeatureInput(prev => newValue);
    setFeatureSuggestionsLoading(prev => true);
    const suggestions = await featureStore.requestSuggestionsAsync(newValue);
    setFeatureSuggestionsLoading(prev => false);
    setFeatureSuggestions(prev => suggestions);
  }
  const featureInputBlurHandler = async () =>
  {
    setFeatureSuggestionsVisible(prev => false);
    await featureInputChangeHandler(selectedFeature.feature?.name || '');
  }
  const selectFeatureHandler = async (x?: Feature) =>
  {
    selectedFeature.feature = x;
    setErrorMessage(prev => '');
    await featureInputChangeHandler(x?.name || '');
  }

  const scopeInputChangeHandler = async (newValue: string) =>
  {
    setScopeInput(prev => newValue);
    setScopeSuggestionsLoading(prev => true);
    const suggestions = await toggleStore.requestScopeSuggestionsAsync(newValue);
    setScopeSuggestionsLoading(prev => false);
    setScopeSuggestions(prev => suggestions);
  }

  const createToggleHandler = async () =>
  {
    setErrorMessage(prev => '');
    if (!selectedFeature.feature || !selectedFeature.feature.id)
    {
      setErrorMessage(prev => uiStore.labels.common.forms.createToggle.errors.featureRequired);
      return;
    }
    await toggleStore.createAsync(selectedFeature.feature.id, scopeInput, isToggleOn);
    if (toggleStore.toggle.isError)
    {
      setErrorMessage(prev => toggleStore.toggle.errorMessage);
    }
    else
    {
      if (onSuccess && selectedFeature.feature)
      {
        onSuccess(selectedFeature.feature);
      }
    }
  }

  return (
    <div>
      <div className="toggle-creator">
        <SuggestionsSelector isLoading={featureSuggestionsLoading}
                            suggestionsVisible={featureSuggestionsVisible}
                            noSuggestionsView={<Message>{uiStore.labels.common.forms.createToggle.feature.suggestionsInstruction}</Message>}
                            suggestions={featureSuggestions}
                            maxSuggestions={10}
                            suggestionKeySelector={x => `${x.id}`}
                            suggestionViewTemplate={x => x.name}
                            onSuggestionSelect={selectFeatureHandler} >
          <WithAddon rightAddon={selectedFeature.feature ? <ActionIcon action={async () => await selectFeatureHandler()}><CancelIcon /></ActionIcon> : <DownArrowIcon />}>
            <input type="text"
                  placeholder={uiStore.labels.common.forms.createToggle.feature.placeholder}
                  value={featureInput}
                  onChange={async e => await featureInputChangeHandler(e.target.value)}
                  onFocus={() => setFeatureSuggestionsVisible(prev => true)}
                  onBlur={() => setTimeout(featureInputBlurHandler, 500)} />
          </WithAddon>
        </SuggestionsSelector>
        <Toggle value={isToggleOn} onClick={() => setIsToggleOn(prev => !prev)} />
        <SuggestionsSelector isLoading={scopeSuggestionsLoading}
                            suggestionsVisible={scopeSuggestionsVisible}
                            noSuggestionsView={<Message>{uiStore.labels.common.forms.createToggle.scope.suggestionsInstruction}</Message>}
                            suggestions={scopeSuggestions}
                            maxSuggestions={10}
                            suggestionKeySelector={x => x}
                            suggestionViewTemplate={x => x}
                            onSuggestionSelect={async x => await scopeInputChangeHandler(x)} >
          <WithAddon leftAddon={<>/</>}>
            <input type="text"
                  placeholder={uiStore.labels.common.forms.createToggle.scope.placeholder}
                  value={scopeInput}
                  onChange={async e => await scopeInputChangeHandler(e.target.value)}
                  onFocus={() => setScopeSuggestionsVisible(prev => true)}
                  onBlur={() => setScopeSuggestionsVisible(prev => false)} />
          </WithAddon>
        </SuggestionsSelector>
        <button onClick={createToggleHandler} disabled={toggleStore.toggle.isLoading}>{uiStore.labels.common.forms.create}</button>
      </div>
      { errorMessage.length > 0 ? <ErrorMessage>{errorMessage}</ErrorMessage> : null }
    </div>
  );
});